import type { NotepadMessage, Session } from "@/services/api.service";

/*
 * A notepad bite's conversation as the studio returns it. The tutor's lines
 * keep its canvas commands inline as <op>{json}</op>; splitNotepadContent
 * separates them so the page shows the spoken text and names each command.
 */

export type NotepadSegment =
  | { kind: "text"; text: string }
  | { kind: "op"; name: string; raw: string };

// An interrupted reply can end inside a command, so a tag may be unclosed.
const OP_TAG = /<op>([\s\S]*?)(?:<\/op>|$)/g;

// The model writes LaTeX with single backslashes ("\\sqrt"), which is not
// valid JSON; the gateway repairs that before parsing. The name is read
// directly, so a command with LaTeX in it is still named.
const OP_NAME = /"op"\s*:\s*"([A-Za-z_]+)"/;

const opName = (raw: string): string => OP_NAME.exec(raw)?.[1] ?? "command";

export const splitNotepadContent = (content: string): NotepadSegment[] => {
  const segments: NotepadSegment[] = [];
  let last = 0;
  for (const match of content.matchAll(OP_TAG)) {
    const text = content.slice(last, match.index).trim();
    if (text) segments.push({ kind: "text", text });
    const raw = (match[1] ?? "").trim();
    segments.push({ kind: "op", name: opName(raw), raw });
    last = match.index + match[0].length;
  }
  const tail = content.slice(last).trim();
  if (tail) segments.push({ kind: "text", text: tail });
  return segments;
};

/** True when the row is a notepad bite: its conversation is in notepad_messages, not messages. */
export const isNotepadSession = (session: Session): boolean =>
  session.notepad_messages != null ||
  session.notepad_messages_unavailable === true ||
  session.notepad_messages_deferred === true;

// Maps, not object literals: a wire string such as "constructor" must not
// resolve to an inherited member.
const SOURCE_LABELS = new Map<string, string>([
  ["voice", "Spoken"],
  ["typed", "Typed"],
  ["frontend", "Sent by the app"],
]);

/** The label for how a student line was written; null when the line does not say. */
export const notepadSourceLabel = (message: NotepadMessage): string | null =>
  (message.source && SOURCE_LABELS.get(message.source)) || null;

// The frontend's two fixed prompts (ai-tutor CanvasVoiceBridge.tsx). Lines
// stored before the gateway stamped `source` carry no source, so these are
// recognized by their exact text.
const APP_PROMPT_TEXTS = new Set([
  "Start teaching. Use your opening phrase to greet the student.",
  "Continue teaching where you left off. Briefly acknowledge you're picking back up.",
]);

/** The frontend's own prompts (kickoff, nudges) sit in the student's role but are not the student. */
export const isAppPrompt = (message: NotepadMessage): boolean =>
  message.role === "user" &&
  (message.source === "frontend" || (!message.source && APP_PROMPT_TEXTS.has(message.content.trim())));

/** A line's time as a Date, or null when the stamp is missing or not a valid time. */
export const notepadLineDate = (tsMs: number | null | undefined): Date | null => {
  if (typeof tsMs !== "number") return null;
  const date = new Date(tsMs);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Clock time of a line, with seconds, so it can be matched to its audio clip. */
export const formatNotepadLineTime = (tsMs: number | null | undefined): string | null =>
  notepadLineDate(tsMs)?.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  }) ?? null;
